// Thin storefront API layer over the Go /api/storefront/** surface. Typed DTOs
// mirror the handler views; the cart token travels in the X-Cart-Token header.

import type { BundleOfferConfig, BundleTierConfig } from "@dk/bundle-ui"
import type { PaymentErrorCode } from "./paymentError"

import { getAttribution, getMarketing } from "./attribution"
import { devStoreHandle } from "./devstore"
import { matchSignals } from "./matchsignals"
import { identityHeaders } from "./visitor"

export interface ProductCard {
  handle: string
  title: string
  product_type: string
  vendor: string
  price_cents?: number
  /** cheapest variant's compare-at price; present ONLY when it exceeds price_cents
   *  (the API omits it otherwise, so its presence alone means "on sale") */
  compare_at_price_cents?: number
  image_src?: string
  /** second image (by position), used for the card "swap" hover effect */
  image_src_hover?: string
  image_thumbhash?: string
  image_width?: number
  image_height?: number
  /** merchant-set display rating in hundredths of a star (0..500); 0 = none */
  rating_avg?: number
  rating_count?: number
  tags?: string[]
}

export interface Variant {
  id: number
  title: string
  sku: string
  option1: string
  option2: string
  option3: string
  price_cents: number
  compare_at_price_cents: number | null
  available: boolean
  /** units remaining when a tracked variant is low (≤ threshold); 0 = hide */
  low_stock?: number
  image_id: number | null
  /** false for a download. Optional because a cached page from before this shipped
   *  does not carry it, and the safe reading of "unknown" is that a parcel exists. */
  requires_shipping?: boolean
}

export interface ProductOption { name: string; values: string[] }
export interface ProductImage { id: number; src: string; alt: string; width?: number; height?: number; thumbhash?: string }

// "Buy more, save more" volume bundle: quantity tiers with a per-unit percent
// discount (basis points) + optional free shipping, plus how the seller chose to
// present it (migration 00061 — headline, caption, badges, which tier opens).
//
// The shapes ARE @dk/bundle-ui's, not a copy of them: that package holds the card
// both this app and the dashboard's bundle editor render, so a field the editor can
// set is by construction a field this app knows how to show.
export type BundleTier = BundleTierConfig

export interface BundleOffer extends BundleOfferConfig {
  /** The product this offer is shown for — echoed back on add so the server can
   *  verify a pairing it issued itself (one offer can target many products). */
  product_id: number
}

export interface Product {
  /** Dikafy's own product id. Klaviyo matches a product event to its catalog
   *  item by external_id, which is this number — see events.ts view_content. */
  id: number
  handle: string
  title: string
  body_html: string
  product_type: string
  vendor: string
  tags: string[]
  rating_avg?: number
  rating_count?: number
  /** Axon taxonomy id for this product; absent = inherit the store-level pick. Only
   *  the Axon pixel consumes it (see lib/tracking/events.ts axonItems). */
  axon_category_id?: number
  seo?: { title?: string; description?: string }
  variants: Variant[]
  options: ProductOption[]
  images: ProductImage[]
  bundle?: BundleOffer
}

/** One published customer review. The payload is served unauthenticated, so it
 *  carries no email, customer id or order id — `verified` is the published signal
 *  that it came from a real buyer. */
export interface Review {
  id: string
  rating: number
  title: string
  body: string
  author: string
  verified: boolean
  created_at: string
  featured?: boolean
  reply?: string
  replied_at?: string
  photos?: { src: string; width: number; height: number; thumbhash?: string }[]
  /** Only on the store-wide list: what this review is about. A store-wide card has to
   *  name its product, or the reader has no idea what is being praised. */
  product?: { title: string; handle: string }
}

/** One bar of the 1–5 breakdown. */
export interface ReviewBucket {
  rating: number
  total: number
}

/** Shopper-side narrowing, shared by the per-product and store-wide review reads. */
export interface ReviewQuery {
  rating?: number
  photos?: boolean
  replied?: boolean
  sort?: string
  limit?: number
  offset?: number
}

export interface Collection {
  handle: string
  title: string
  description: string
  image_src: string
  sort_order: string
}

// Collection index card (the /collections list): no products, plus a member count.
export interface CollectionCard {
  handle: string
  title: string
  description: string
  image_src: string
  product_count: number
}

export interface ArticleCard {
  handle: string
  title: string
  excerpt: string
  image_src?: string
  image_thumbhash?: string
  author?: string
  tags?: string[]
  published_at?: string | null
}
export interface Article extends ArticleCard {
  body_html: string
}

export interface Page {
  handle: string
  title: string
  body_html: string
}

export interface CartLine {
  /** cart_items row id — how a line is addressed for quantity/remove */
  line_id?: number
  variant_id: number
  product_id: number
  product_handle: string
  title: string
  variant_title: string
  sku: string
  price_cents: number
  compare_at_price_cents: number | null
  quantity: number
  line_total_cents: number
  available: boolean
  image_src?: string
  /** buyer-supplied line fields (engraving, gift note); omitted when empty */
  properties?: Record<string, string>
  /** Axon taxonomy id for this line's product; absent = inherit the store's. */
  axon_category_id?: number
  /** the volume-bundle pool pricing this line, when one does */
  bundle_id?: number
  /** the personalization this line carries, when one does. Its price is already
   *  folded into price_cents. */
  cz_design_id?: number
  /** the same personalization's unguessable public handle — what addresses it from
   *  OUTSIDE the cart: the "Edit personalization" link, and the preview image URL
   *  that is already this line's image_src. */
  cz_design_token?: string
  /** Present ONLY when the line is charged at more than one price — a volume
   *  bundle splitting it across packs. When it is, `line_total_cents` is the
   *  money and `price_cents` is just the first bucket: do not multiply. */
  buckets?: CartLineBucket[]
  /** the line's value at list price; absent when nothing discounted it */
  original_total_cents?: number
}

/** One run of units inside a line that share a charged price. */
export interface CartLineBucket {
  quantity: number
  unit_cents: number
  original_cents: number
  /** false for a leftover unit — it filled no pack and paid list price */
  in_bundle: boolean
}

/** One tier pack inside a bundle pool. */
export interface CartBundlePack {
  tier_id: number
  quantity: number
  discount_bps: number
  label: string
  free_shipping: boolean
}

/** A priced volume-bundle position: every unit of ONE product an offer applies to,
 *  decomposed into tier packs.
 *
 *  Entirely derived — the buyer's clicked tier is not what they are charged. It
 *  carries no units of its own: the units ARE cart lines (`line_ids` names them),
 *  which is what lets each one keep a quantity stepper. */
export interface CartBundle {
  bundle_id: number
  product_id: number
  /** the offer heading ("Buy more, save more") — NOT what a line is called */
  title: string
  product_title: string
  product_handle: string
  packs: CartBundlePack[]
  /** the packing as the header says it: "3", "3 + 1", "4 + 4" */
  pack_label: string
  packed_qty: number
  /** units that filled no pack and pay list price */
  remainder_qty: number
  line_ids: number[]
  subtotal_cents: number
  original_total_cents: number
  savings_cents: number
  free_shipping: boolean
  available: boolean
}

export interface Cart {
  token: string
  currency: string
  lines: CartLine[]
  /** volume-bundle pools pricing some of `lines` — headers, not contents */
  bundles: CartBundle[]
  /** free gift-with-purchase line (0-price) when the subtotal earns it (server-decided) */
  gift?: CartLine
  item_count: number
  subtotal_cents: number
  requires_shipping: boolean
  free_shipping: boolean
  /** Price and shipping A/B arms this cart is charged under (lib/displayPrice). */
  experiments?: Array<{ key: string; kind: string; arm: number; prices?: Record<string, number>; threshold_cents?: number }>
}

// ---- checkout / payment ----------------------------------------------------

/** The seller-authored checkout layout resolved for this shopper. `sections` is a
 *  theme SectionInstance[]; an empty array means the store has authored no design
 *  and the storefront renders its built-in layout. */
export interface CheckoutDesignDTO {
  funnel_id: number | null
  page_id: number | null
  sections: import("@dk/theme").SectionInstance[]
  /** The tip rule for this shopper's funnel, resolved server-side with the store's
   *  own as the fallback. null when the server did not answer. Deliberately the only
   *  money rule published here — pp15 is a hidden surcharge and stays server-side. */
  tipping: unknown
}

export interface CheckoutLine {
  variant_id: number
  product_id: number
  title: string
  variant_title: string
  sku: string
  price_cents: number
  quantity: number
  line_total_cents: number
  /** variant image, else the product's first; omitted when the product has none */
  image_src?: string
  /** offer heading when the line came out of a bundle; omitted otherwise */
  bundle_title?: string
  /** pre-discount unit price; only present when it beats price_cents */
  original_price_cents?: number
  /** Axon taxonomy id for this line's product; absent = inherit the store's, and
   *  also absent on a manual draft line, which has no product at all. */
  axon_category_id?: number
  /** Buyer-supplied line text (personalization, engraving, gift note); omitted
   *  when the line carries none. */
  properties?: Record<string, string>
}

export interface Checkout {
  token: string
  status: string
  email: string
  currency: string
  lines: CheckoutLine[]
  subtotal_cents: number
  discount_cents: number
  shipping_cents: number
  tax_cents: number
  /** Buyer's tip. Already INCLUDED in total_cents — never add it again. */
  tip_cents: number
  total_cents: number
  discount_code: string
  revision: number
  shipping_line?: { rate_id?: number; title?: string; price_cents?: number } | null
}

export interface ShippingRate {
  rate_id: number
  name: string
  price_cents: number
  /** The zone's pre-selected method. At most one rate in a list carries it, and
      none does when the seller has not chosen one — see checkout/validation.ts. */
  default?: boolean
}

export interface Address {
  first_name?: string
  last_name?: string
  address1?: string
  /** Apartment / suite / unit. Stored in the address blob and printed by the
      seller's order detail + the thank-you page, so it reaches the parcel. */
  address2?: string
  city?: string
  province?: string
  zip?: string
  country?: string
  phone?: string
}

export interface StartPayment {
  transaction_id: number
  provider: string
  /**
   * The provider's own intent id. Airwallex's confirm() needs it ALONGSIDE the
   * client secret; Stripe's secret embeds its intent id and PayPal's IS the order
   * id, so no other flow reads this.
   */
  external_id?: string
  client_secret: string
  provider_status: string
  /** Set by providers that approve OFF-SITE (PayPal): send the buyer here. */
  redirect_url?: string
  public_config: { publishable_key?: string; client_id?: string } | null
}

/**
 * Work the browser has to do before a payment can finish. Returned instead of an order
 * when a defer-confirm payment needs 3DS: nothing was authorized at checkout, so the
 * card is only asked at the END of the funnel — and by then the buyer is on the upsell
 * page and CAN answer the challenge. Run it against `client_secret`, then call the same
 * endpoint again.
 */
export interface PaymentAction {
  type: "confirm_payment"
  transaction_id: number
  client_secret: string
}

export interface PaymentMethodPublic {
  provider: string
  display_name: string
  mode: string
  public_config: { publishable_key?: string; client_id?: string } | null
  /**
   * The store may offer an on-page card form for this provider (PayPal ACDC): the
   * account is eligible AND the seller turned it on. Still only permission to ASK —
   * the SDK's own isEligible() decides whether a field is rendered, because
   * eligibility can change without anything telling us.
   */
  card_fields?: boolean
  /**
   * Express wallets this gateway can render a button for on THIS hostname:
   * "apple_pay" | "google_pay" | "paypal". Apple Pay is absent on a host the seller's
   * gateway account has not registered, because an unregistered domain gives no error
   * the buyer can act on — the sheet just never opens, leaving a dead button.
   *
   * The checkout used to decide express by testing for the PayPal provider name, which
   * no second gateway could ever satisfy.
   */
  wallets?: string[]
  /**
   * A payment through this gateway can carry a post-purchase upsell offer. The
   * storefront does not branch on it — the server decides whether a funnel opens — it
   * is here because it is the same fact the dashboard shows the seller.
   */
  upsell_capable?: boolean
}

/**
 * GET /order/:token — the public (thank-you-page) view of a placed order. Mirrors Go
 * internal/domain/order.PublicOrder and is deliberately PII-FREE — no email, phone
 * or address — because the page URL, token included, is reported to every configured
 * ad network. The contact/delivery half of the confirmation comes from the browser's
 * own stash instead (lib/orderconfirm).
 */
export interface PublicOrder {
  token: string
  order_number: number
  /** The number as the STORE renders it (order_number_display, 00068) — the
   *  seller's own prefix/suffix, snapshotted when the order was created, and the
   *  same string the buyer's receipt email carries. Render this; never compose
   *  `#${order_number}`, which ignores the setting. */
  order_number_display: string
  currency: string
  subtotal_cents: number
  discount_cents: number
  shipping_cents: number
  tax_cents: number
  /** Buyer's tip, already inside total_cents. Absent when they did not tip. */
  tip_cents?: number
  total_cents: number
  discount_code: string
  financial_status: string
  fulfillment_status: string
  /** when the buyer paid (falls back to when the order was created) */
  placed_at: string
  email_sha256: string
  /** Buyer's phone in E.164, SHA-256'd like the email. Absent when the order has
   *  none, or when it could not be completed to an international number. */
  phone_sha256?: string
  /** Stable per-customer id for advanced matching. Absent on a guest order. */
  customer_ref?: string
  /** The ad network that drove the visit this order came from — the adapter id
   *  (`meta` | `google` | `tiktok` | `pinterest` | `axon`), or one of the networks
   *  this storefront has no adapter for. Resolved server-side from the landing
   *  URL's click ids and utm_source. Absent when the traffic carried no evidence of
   *  an ad click, which the tracker reads as "every configured pixel". */
  ad_source?: string
  lines: {
    variant_id: number
    product_id: number
    sku: string
    title: string
    variant_title: string
    quantity: number
    price_cents: number
    /** variant image, else the product's first — ad networks want it on the
     *  conversion item (Axon lists image_url as a recommended item field) */
    image_src?: string
    /** the offer that discounted this line; omitted on an ordinary line */
    bundle_title?: string
    /** pre-discount unit price; only present when it beats price_cents */
    original_price_cents?: number
    /** Axon taxonomy id for this line's product; absent = inherit the store's. The
     *  purchase item must report the same one view_item did, or Axon cannot tie the
     *  conversion back to the product view. */
    axon_category_id?: number
  }[]
}

/** The pixel layer's name for the same payload — it was this type's first consumer. */
export type TrackedOrder = PublicOrder

export interface OrderConfirm {
  token: string
  order_number: number
  /** The number as the STORE renders it (order_number_display, 00068) — the
   *  seller's own prefix/suffix, snapshotted when the order was created, and the
   *  same string the buyer's receipt email carries. Render this; never compose
   *  `#${order_number}`, which ignores the setting. */
  order_number_display: string
  email: string
  currency: string
  total_cents: number
  financial_status: string
  fulfillment_status: string
}

// ---- upsell ----------------------------------------------------------------

export interface UpsellOffer {
  offer_id: number
  position: number
  variant_id: number
  product_title: string
  variant_title: string
  price_cents: number
  /** server-authoritative pre-discount price, for a struck-through compare-at */
  list_price_cents?: number
  quantity: number
  /** variant's own image, else the product's first; absent when it has none */
  image_src?: string
  image_thumbhash?: string
  /** The offered product's option axes + every sellable sibling, already priced by
   *  this step's own discount. Both absent for a single-variant product — there is
   *  nothing to choose, so the card renders no picker. */
  options?: UpsellOfferOption[]
  variants?: UpsellOfferVariant[]
}

/** One option axis of the offered product (Colour, Size…), in the seller's order. */
export interface UpsellOfferOption {
  name: string
  values: string[]
}

/** One sellable variant of the offered product. `price_cents` is the DISCOUNTED unit
 *  price for this step, computed server-side, so switching cannot display a figure
 *  the accept would then refuse. */
export interface UpsellOfferVariant {
  variant_id: number
  title: string
  /** the variant's value on each axis, positionally matching `options` */
  option_values: string[]
  price_cents: number
  list_price_cents: number
  available: boolean
  image_src?: string
  image_thumbhash?: string
}

/** What the buyer settled on before pressing accept. */
export interface UpsellSelection {
  variantId: number
  quantity: number
}

// A theme SectionInstance the funnel's upsell page is built from. Kept as a loose
// local shape (not the @dk/theme type) so the storefront doesn't pull the theme
// package into this DTO module; the upsell renderer narrows it where needed.
export interface UpsellSectionInstance {
  id: string
  type: string
  visible?: boolean
  settings: Record<string, unknown>
  blocks: { id: string; type: string; visible?: boolean; settings: Record<string, unknown> }[]
}

export interface UpsellSession {
  token: string
  expires_at: string
  offer: UpsellOffer | null
  /** the funnel's custom upsell-page design; empty [] = the default fixed layout */
  page?: UpsellSectionInstance[]
}

// Order reference returned when a funnel finishes (held auth captured → one order).
export interface OrderRef {
  token: string
  order_number: number
  /** The number as the STORE renders it (order_number_display, 00068) — the
   *  seller's own prefix/suffix, snapshotted when the order was created, and the
   *  same string the buyer's receipt email carries. Render this; never compose
   *  `#${order_number}`, which ignores the setting. */
  order_number_display: string
}

export interface UpsellStep {
  status: string // accepted | declined | unavailable
  done: boolean // funnel finished (or expired) → order captured
  next: UpsellOffer | null // the next offer to show, if any
  /** The RESOLVED design for `next` — a step may carry its own, so it can differ
   *  from the one the session opened with. Absent from an older server, which is
   *  why nextPage() keeps the current one rather than blanking it. */
  page?: UpsellSectionInstance[]
  order?: OrderRef // the created order, present when done + captured
  /** Present when `done` but the payment needs the buyer first — the same
   *  PaymentAction the checkout's /approved returns, and the case that interface's
   *  own comment describes: a defer-confirm card is confirmed at the END of the
   *  funnel, so the challenge lands on the upsell page. Answer it against
   *  `client_secret`, then call accept/decline again (the server is idempotent). */
  action?: PaymentAction
}

// approvedPayment response: after the buyer's authorization is verified, either a
// pre-capture upsell funnel opens (upsell present) or the order is captured now.
export interface ApproveResult {
  upsell?: UpsellSession
  order?: OrderConfirm
}

// ---- account ---------------------------------------------------------------

export interface Customer {
  id: number
  email: string
  first_name: string
  last_name: string
  accepts_marketing: boolean
}

export interface Tokens {
  access_token: string
  refresh_token: string
  expires_in: number
}

export interface OrderSummary {
  token: string
  order_number: number
  /** The number as the STORE renders it (order_number_display, 00068) — the
   *  seller's own prefix/suffix, snapshotted when the order was created, and the
   *  same string the buyer's receipt email carries. Render this; never compose
   *  `#${order_number}`, which ignores the setting. */
  order_number_display: string
  currency: string
  total_cents: number
  financial_status: string
  fulfillment_status: string
  created_at: string
}

export interface OrderLineItem {
  title: string
  variant_title: string
  quantity: number
  price_cents: number
  source: string
}

const BASE = "/api/storefront"

export class ApiError extends Error {
  /**
   * The API's own explanation, when it sent one. Separate from `message` (which
   * stays the HTTP status line) because most callers map `status` to their own copy
   * and must not start showing raw server text. Use it only where the server's
   * reason is the point — e.g. a rejected tip, where "not enabled", "over the
   * limit" and "not one of the offered amounts" need different answers.
   */
  detail = ""

  constructor(
    public status: number,
    message: string,
    detail = "",
  ) {
    super(message)
    this.detail = detail
  }
}

// Registered by the bootstrap store: api.ts cannot import it (the store imports
// this module's types), and a getter keeps the header reading the payload the
// page is rendering from right now.
let displayedMoneyTests: () => string = () => ""
export function setMoneyTestSource(fn: () => string): void {
  displayedMoneyTests = fn
}

export async function req<T>(
  path: string,
  opts: { method?: string; body?: unknown; cartToken?: string; token?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {}
  if (opts.body !== undefined) headers["Content-Type"] = "application/json"
  if (opts.cartToken) headers["X-Cart-Token"] = opts.cartToken
  if (opts.token) headers["Authorization"] = `Bearer ${opts.token}`
  // First-party identity and this visit's id. Sent on every call so the server can
  // bind a cart to this browser and attach an address the moment one is EARNED (a
  // newsletter sign-up, a checkout contact) — the client itself never asserts who
  // it is — and so a cart created by this request is stamped with the session that
  // built it, in its own INSERT.
  Object.assign(headers, identityHeaders())
  // The money A/B tests this page displayed, on cart calls only (lib/experiment).
  if (path.startsWith("/cart")) {
    const exp = displayedMoneyTests()
    if (exp) headers["X-DK-Exp"] = exp
  }
  // Dev-only: forward the previewed store so the shared localhost proxy resolves it.
  const devStore = devStoreHandle()
  if (devStore) headers["X-Store-Handle"] = devStore
  const res = await fetch(BASE + path, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })
  if (!res.ok) {
    // Read the API's `message` so a caller that needs the reason can show it. Best
    // effort: an error body is not guaranteed to be JSON.
    let detail = ""
    try {
      const body = (await res.json()) as { message?: string }
      if (typeof body?.message === "string") detail = body.message
    } catch {
      /* non-JSON error body — the status alone has to do */
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, detail)
  }
  return (await res.json()) as T
}

export const api = {
  products: (limit = 24) => req<{ products: ProductCard[] }>(`/products?limit=${limit}`),
  product: (handle: string) => req<{ product: Product }>(`/products/${encodeURIComponent(handle)}`),
  collection: (handle: string, opts: { sort?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.sort) q.set("sort", opts.sort)
    if (opts.limit != null) q.set("limit", String(opts.limit))
    if (opts.offset != null) q.set("offset", String(opts.offset))
    const qs = q.toString()
    return req<{ collection: Collection; products: ProductCard[]; total: number }>(
      `/collections/${encodeURIComponent(handle)}${qs ? `?${qs}` : ""}`,
    )
  },
  collections: () => req<{ collections: CollectionCard[] }>(`/collections`),
  search: (q: string) => req<{ query: string; products: ProductCard[] }>(`/search?q=${encodeURIComponent(q)}`),
  related: (handle: string, limit = 8) =>
    req<{ products: ProductCard[] }>(`/products/${encodeURIComponent(handle)}/related?limit=${limit}`),
  articles: (limit = 12, offset = 0) =>
    req<{ articles: ArticleCard[] }>(`/blog?limit=${limit}&offset=${offset}`),
  article: (handle: string) => req<{ article: Article }>(`/blog/${encodeURIComponent(handle)}`),
  page: (handle: string) => req<{ page: Page }>(`/pages/${encodeURIComponent(handle)}`),

  /** Newsletter sign-up / contact enquiry. `website` is the honeypot: it must be
   *  sent empty — a filled one is silently accepted and dropped server-side. */
  submitForm: (
    type: "newsletter" | "contact",
    body: { email: string; name?: string; message?: string; website?: string },
  ) =>
    req<{ status: boolean }>(`/forms/${type}`, {
      method: "POST",
      body: { ...body, source_url: location.pathname + location.search },
    }),

  getCart: (token: string) => req<{ cart: Cart }>(`/cart`, { cartToken: token }),
  addItem: (token: string, variantId: number, quantity = 1, properties?: Record<string, string>, designId?: number) =>
    // JSON.stringify (in req) drops an undefined-valued key entirely, so a
    // plain conditional expression sends the identical wire shape as the
    // spread-in-a-fresh-object form without the extra object allocations.
    req<{ cart: Cart }>(`/cart/items`, {
      method: "POST",
      body: {
        variant_id: variantId, quantity,
        properties: properties && Object.keys(properties)[0] ? properties : undefined,
        design_id: designId || undefined,
      },
      cartToken: token,
    }),
  setItem: (token: string, variantId: number, quantity: number) =>
    req<{ cart: Cart }>(`/cart/items/${variantId}`, { method: "PUT", body: { quantity }, cartToken: token }),
  removeItem: (token: string, variantId: number) =>
    req<{ cart: Cart }>(`/cart/items/${variantId}`, { method: "DELETE", cartToken: token }),
  // Line-keyed forms. One variant can occupy several lines once buyer text is in
  // play (different engravings), so variant_id alone can no longer address one.
  setLine: (token: string, lineId: number, quantity: number) =>
    req<{ cart: Cart }>(`/cart/lines/${lineId}`, { method: "PUT", body: { quantity }, cartToken: token }),
  removeLine: (token: string, lineId: number) =>
    req<{ cart: Cart }>(`/cart/lines/${lineId}`, { method: "DELETE", cartToken: token }),
  /**
   * `productId` is which product the offer is being bought for: one offer can target
   * many products, so the server cannot derive it — and it re-verifies the pairing
   * rather than trusting it.
   */
  addBundle: (token: string, bundleId: number, productId: number, tierId: number, variantIds: number[]) =>
    req<{ cart: Cart }>(`/cart/bundle-items`, {
      method: "POST",
      body: { bundle_id: bundleId, product_id: productId, tier_id: tierId, variant_ids: variantIds },
      cartToken: token,
    }),
  /** "Remove" on a bundle header: drops every line of that pool. Both ids are
   *  needed because a pool is ONE product's position under one offer — an offer
   *  targeting a collection prices each of its products separately. */
  removeBundle: (token: string, bundleId: number, productId: number) =>
    req<{ cart: Cart }>(`/cart/bundle-items/${bundleId}/${productId}`, { method: "DELETE", cartToken: token }),

  // checkout (draft order) — all totals are server-computed. UTM (marketing
  // attribution) is attached here so it snapshots onto the draft → order.
  // country hint: prod resolves geo from the Cloudflare `Cf-Ipcountry` header
  // automatically; in dev a `?dk_country=US` query param forces the checkout zone.
  // The seller-authored checkout LAYOUT for this shopper (checkout zone → funnel →
  // that funnel's page). Fired in PARALLEL with createCheckout — it must not be a
  // field on that response, because the checkout paints its form before the draft
  // lands and shipping the layout with the draft would reflow the page at exactly
  // the moment the buyer decided to pay. Same `dk_country` dev override as below.
  checkoutDesign: (cartToken: string, country?: string) => {
    const q = new URLSearchParams()
    if (cartToken) q.set("cart", cartToken)
    const c = country || new URLSearchParams(window.location.search).get("dk_country") || ""
    if (c) q.set("country", c)
    const qs = q.toString()
    return req<{ design: CheckoutDesignDTO }>(`/checkout/design${qs ? `?${qs}` : ""}`, { cartToken })
  },

  createCheckout: (cartToken: string, email = "", note = "") => {
    const country = new URLSearchParams(window.location.search).get("dk_country") || undefined
    return req<{ checkout: Checkout }>(`/checkout`, {
      method: "POST",
      body: {
        cart_token: cartToken,
        email,
        note,
        utm: getAttribution(),
        // The rest of what the landing URL said — the GA4 utm fields, every
        // network's click id, the referrer. The server resolves the order's
        // ad_source from these; it is deliberately not something this body can
        // nominate.
        marketing: getMarketing(),
        // Ad-platform click/browser ids, so the server-side purchase event can be
        // matched to the same person as the browser pixel.
        tracking: matchSignals(),
        ...(country ? { country } : {}),
      },
      cartToken,
    })
  },
  getCheckout: (token: string) => req<{ checkout: Checkout }>(`/checkout/${token}`),
  updateContact: (token: string, email: string, shipping: Address, marketingConsent = false) =>
    req<{ checkout: Checkout }>(`/checkout/${token}/contact`, {
      method: "PUT",
      body: { email, shipping_address: shipping, marketing_consent: marketingConsent },
    }),
  applyDiscount: (token: string, code: string) =>
    req<{ checkout: Checkout }>(`/checkout/${token}/discount`, { method: "POST", body: { code } }),
  paymentMethods: () => req<{ methods: PaymentMethodPublic[] }>(`/payment-methods`),
  listShippingRates: (token: string) =>
    req<{ rates: ShippingRate[] }>(`/checkout/${token}/shipping-rates`),
  setShippingRate: (token: string, rateId: number) =>
    req<{ checkout: Checkout }>(`/checkout/${token}/shipping-rate`, { method: "POST", body: { rate_id: rateId } }),
  /**
   * Set (or clear, with 0) the buyer's tip. The amount is validated server-side
   * against the store's rule and the goods subtotal, so a rejection is a 422 whose
   * message is meant to be shown.
   */
  setTip: (token: string, tipCents: number) =>
    req<{ checkout: Checkout }>(`/checkout/${token}/tip`, { method: "PUT", body: { tip_cents: tipCents } }),

  // payment (deferred capture) — start creates an authorize-only intent; the buyer
  // approves (funds held, not captured); approve verifies the hold and either opens
  // a pre-capture upsell funnel or captures + converts the draft → order now.
  // `express` asks the wallet to collect the buyer's address (they paid from the
  // cart, not the form), which also means the amount is not final yet — see
  // expressCheckout below for the step that completes it. `popup` tells the server
  // to build return URLs for a child window rather than this tab.
  startPayment: (
    token: string,
    provider: string,
    opts: {
      express?: boolean
      popup?: boolean
      cardFields?: boolean
      /**
       * A payment method the browser already tokenized (a Stripe `pm_…`, an Airwallex
       * payment consent + its customer). Offering one lets the server create an
       * UNCONFIRMED intent, which is what keeps the amount editable for a
       * post-purchase upsell on a gateway that freezes it at authorization.
       *
       * It is a permission, not an instruction: the server still picks the capture
       * model, and prefers holding the buyer's funds whenever no funnel needs the
       * amount to stay mutable.
       */
      paymentMethodToken?: string
      paymentMethodCustomer?: string
      /**
       * The express wallet BRAND the sheet reported ("apple_pay" / "google_pay" / …),
       * recorded on the transaction as the payment ROUTE (00071) instead of just
       * "express". A hint, not an instruction — the server only trusts a value this
       * provider's own capabilities actually advertise.
       */
      wallet?: string
    } = {},
  ) =>
    req<{ payment: StartPayment }>(`/checkout/${token}/payment`, {
      method: "POST",
      // cardFields asks for a differently-shaped intent (a PayPal order that names
      // the card source), not a different presentation — see the server's
      // WithCardFields.
      body: {
        provider,
        express: !!opts.express,
        popup: !!opts.popup,
        card_fields: !!opts.cardFields,
        payment_method_token: opts.paymentMethodToken || "",
        payment_method_customer: opts.paymentMethodCustomer || "",
        wallet: opts.wallet || "",
      },
    }),
  // Express return leg: the server reads the address back off the approved wallet
  // order, applies it to the draft (tax), picks a shipping rate, and re-prices the
  // held authorization. Only then is it safe to approve.
  expressCheckout: (token: string, transactionId: number, marketingConsent = false) =>
    req<{ checkout: Checkout; email: string; shipping_address: Address }>(`/checkout/${token}/express`, {
      method: "POST",
      body: { transaction_id: transactionId, marketing_consent: marketingConsent },
    }),
  approvePayment: (token: string, transactionId: number) =>
    req<{ upsell?: UpsellSession; order?: OrderConfirm; action?: PaymentAction }>(`/checkout/${token}/approved`, {
      method: "POST",
      body: { transaction_id: transactionId },
    }),

  // The opposite leg of approvePayment: telling the server about a refusal it
  // could not see. Every gateway SDK confirms straight from the checkout page,
  // so a declined card never reaches /approved and the transaction stays
  // 'pending' with an empty error — indistinguishable, on the seller's list,
  // from a shopper who wandered off.
  //
  // Sends a CLASSIFIED code, never a gateway message: the provider's own text
  // can carry an internal endpoint, an order id and a debug id (see
  // lib/paymentError.ts), and posting it here would put that on a dashboard
  // page instead of on this one. The server re-validates the code anyway.
  reportPaymentFailure: (token: string, reason: PaymentErrorCode) =>
    req<{ status: boolean }>(`/checkout/${token}/failed`, {
      method: "POST",
      body: { reason },
    }),

  /**
   * The FULL account of the same failure, for the seller's Abandoned checkouts
   * timeline — a separate call from reportPaymentFailure above, which stays
   * classified-code-only because `transactions.error` must never carry a gateway
   * message. `message` is the gateway's raw text, deliberately: it is written for
   * whoever has to fix the problem, and that is the seller, never the shopper.
   * Nothing here is trusted server-side — the draft supplies the amount and the
   * store, the code is checked against the closed set, and the text is capped.
   */
  reportPaymentFailureDetail: (
    token: string,
    body: {
      transaction_id: number
      provider: string
      payment_method: string
      code: string
      message: string
      card_brand: string
      card_last4: string
    },
  ) => req<{ recorded: boolean }>(`/checkout/${token}/payment-failed`, { method: "POST", body }),

  // The one-click post-purchase upsell calls live in lib/upsellApi.ts — they are
  // reached only from the lazy checkout chunk, and this object is entry-resident.

  // customer account
  register: (body: {
    email: string
    password: string
    first_name?: string
    last_name?: string
    accepts_marketing?: boolean
  }) =>
    req<{ customer: Customer; tokens: Tokens }>(`/account/register`, { method: "POST", body }),
  login: (email: string, password: string) =>
    req<{ customer: Customer; tokens: Tokens }>(`/account/login`, { method: "POST", body: { email, password } }),
  refresh: (refreshToken: string) =>
    req<{ tokens: Tokens }>(`/account/refresh`, { method: "POST", body: { refresh_token: refreshToken } }),
  logout: (refreshToken: string) =>
    req<{ status: boolean }>(`/account/logout`, { method: "POST", body: { refresh_token: refreshToken } }),
  me: (token: string) => req<{ customer: Customer }>(`/account/me`, { token }),
  myOrders: (token: string) => req<{ orders: OrderSummary[] }>(`/account/orders`, { token }),
  myOrder: (token: string, orderToken: string) =>
    req<{ order: OrderSummary; line_items: OrderLineItem[] }>(`/account/orders/${orderToken}`, { token }),
  // Thank-you page read, keyed by the order's unguessable token — works for guests
  // and after a refresh, unlike the in-memory placed-order state. PII-free.
  publicOrder: (orderToken: string) => req<{ order: PublicOrder }>(`/order/${orderToken}`),
  /**
   * Claims this order's ONE browser-side Purchase conversion; `first` is true for
   * exactly one view of the thank-you page, ever, across every device.
   *
   * It has to be the server, because the case that produced duplicates is one no
   * client store can see: the receipt email links to /order/{token}, and a buyer
   * opening it on their phone is a browser that never held the first guard.
   *
   * A POST because it WRITES — calling it consumes the claim. Do not fold it into
   * publicOrder(); a second reader of that GET would silently burn a real
   * conversion.
   */
  claimPurchaseView: (orderToken: string) =>
    req<{ first: boolean }>(`/order/${orderToken}/purchase-view`, { method: "POST" }),

  // First-party identity: the server mints the profile token (see lib/visitor).
  // The current cart token rides along so a cart filled before the profile
  // existed is bound to it too.
  ensureVisitor: () =>
    req<{ visitor: { token: string } }>(`/visitor`, { method: "POST", cartToken: cartTokenForVisitor() }),
}

// cartTokenForVisitor reads the persisted cart token without importing the cart
// store (which imports this module). Absent is fine — it just means there is no
// cart to bind yet.
function cartTokenForVisitor(): string {
  try {
    return localStorage.getItem(`dk:cart:${location.host}`) || ""
  } catch {
    return ""
  }
}
